import { ErrorBoundaryFallback } from '@sparklayer-io/spark-dash-core/components/error-boundary-fallback';
import { EnvProvider } from '@sparklayer-io/spark-dash-core/context/env-context';
import {
  type BuildApis,
  type HandlersInput as CoreHandlersInput,
  PageContext,
  type SiteType,
  type UserType,
  usePageContextValue,
  type WithPageProps,
} from '@sparklayer-io/spark-dash-core/context/page-context';
import { SettingsProvider } from '@sparklayer-io/spark-dash-core/context/settings-context';
import { queryClient as defaultQueryClient } from '@sparklayer-io/spark-dash-core/lib/query-client';
import {
  type NavigateFn,
  NavigationProvider,
} from '@sparklayer-io/spark-dash-ui/context/navigation-context';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ComponentType, useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { buildApis as defaultBuildApis } from '../api/build-apis';
import { SetupStepsProvider } from '../features/onboarding/setup/setup-steps-context';
import type { OnboardingChecklistStep } from '../features/onboarding/shared/types';
import i18n from '../i18n';

// The contexts themselves live in `@sparklayer-io/spark-dash-core`. What lives here is the
// composition: the order the providers stack in, this package's apis as the default, and the
// onboarding steps provider, which is a feature of this package rather than core plumbing.
//
// `i18n` is handed to `I18nextProvider` rather than imported for its side effect. Registering the
// namespaces is a side effect, but every screen goes through this wrapper, so passing the instance
// as a prop makes the registration a value dependency of the rendered tree: no `sideEffects` entry
// has to survive a bundler's tree-shaking for the translations to be there.
export * from '@sparklayer-io/spark-dash-core/context/page-context';

/** `buildApis` is optional here because this package can default it to its own composition root. */
type HandlersInput = Omit<CoreHandlersInput, 'buildApis'> & { buildApis?: BuildApis };

/**
 * To be supplied by consumers of `withPageProviders` to initialize context
 */
type PageProvidersInput = {
  site: SiteType;
  handlers: HandlersInput;
  user?: UserType;
  /** Platform's setup steps, read via `useSetupSteps`. Omit for the platform-agnostic defaults. */
  setupSteps?: OnboardingChecklistStep[];
};

export type WithPageProvidersProps<TPageProps extends Record<string, unknown>> = WithPageProps<
  PageProvidersInput,
  TPageProps
> & { queryClient?: QueryClient };

// Stable reference for the default - avoids handing consumers a fresh `{}` each render.
const DEFAULT_PAGE_PROPS = {} as Record<string, unknown>;

// biome-ignore lint/complexity/noBannedTypes: `{}` default means "no page props" and keeps the WithPageProps emptiness check working
export function withPageProviders<TPageProps extends Record<string, unknown> = {}>(
  Component: ComponentType<TPageProps>,
) {
  return function WrappedWithPageProviders(wrapperProps: WithPageProvidersProps<TPageProps>) {
    const {
      site,
      handlers,
      setupSteps,
      pageProps = DEFAULT_PAGE_PROPS as TPageProps,
      queryClient = defaultQueryClient,
      user,
    } = wrapperProps;
    const contextValue = usePageContextValue(
      site,
      { ...handlers, buildApis: handlers.buildApis ?? defaultBuildApis },
      user,
    );
    // Bound so the handler keeps its `this`, and stable so primitives below don't re-render.
    const navigate = useMemo<NavigateFn>(
      () => (path, options) => contextValue.handlers.navigationHandler.navigate(path, options),
      [contextValue.handlers.navigationHandler],
    );

    return (
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <PageContext.Provider value={contextValue}>
            <NavigationProvider navigate={navigate}>
              <ErrorBoundaryFallback
                notificationHandler={contextValue.handlers.notificationHandler}
              >
                <EnvProvider>
                  <SettingsProvider>
                    <SetupStepsProvider steps={setupSteps}>
                      <Component {...pageProps} />
                    </SetupStepsProvider>
                  </SettingsProvider>
                </EnvProvider>
              </ErrorBoundaryFallback>
            </NavigationProvider>
          </PageContext.Provider>
        </QueryClientProvider>
      </I18nextProvider>
    );
  };
}
